import { motion, AnimatePresence } from "framer-motion";
import { OptionCard, ChecklistItems } from "../shared";
import { MarkdownContent } from "@/components/ui/markdown-content";
import type {
  ChooseChecklistSpec,
  ChooseChecklistAnswer,
} from "@/types/hld-wizard";

const fadeIn = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.3 },
};

interface ChooseChecklistDecisionProps {
  spec: ChooseChecklistSpec;
  context: string;
  question: string;
  answer: ChooseChecklistAnswer | undefined;
  onChange: (answer: ChooseChecklistAnswer) => void;
  disabled?: boolean;
}

export function ChooseChecklistDecision({
  spec,
  context,
  question,
  answer,
  onChange,
  disabled,
}: ChooseChecklistDecisionProps) {
  const selectedOptionId = answer?.option_id ?? "";

  const checklistItems = spec.checklist.adaptive
    ? spec.checklist.sets[selectedOptionId] ?? []
    : spec.checklist.sets["_default"] ?? [];

  function selectOption(optionId: string) {
    onChange({ option_id: optionId, checked_ids: [] });
  }

  function toggleChecklist(id: string) {
    if (!answer) return;
    const current = answer.checked_ids;
    const next = current.includes(id)
      ? current.filter((c) => c !== id)
      : [...current, id];
    onChange({ ...answer, checked_ids: next });
  }

  return (
    <div className="space-y-6">
      {context && (
        <div className="border-l-2 border-primary/30 pl-4 text-muted-foreground">
          <MarkdownContent>{context}</MarkdownContent>
        </div>
      )}

      <MarkdownContent className="font-medium">{question}</MarkdownContent>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {spec.options.map((opt) => (
          <OptionCard
            key={opt.id}
            option={opt}
            selected={selectedOptionId === opt.id}
            onClick={() => selectOption(opt.id)}
            disabled={disabled}
          />
        ))}
      </div>

      <AnimatePresence>
        {selectedOptionId && checklistItems.length > 0 && (
          <motion.div {...fadeIn} className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">
              {spec.checklist.question}
            </p>
            <ChecklistItems
              items={checklistItems}
              checked={answer?.checked_ids ?? []}
              onToggle={toggleChecklist}
              disabled={disabled}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
